
import React, { useState } from 'react';
import {
  Form,
  Input,
  InputNumber,
  Row,
  Col,
  Checkbox,
  Select,
  Button,
  Upload,
  Alert,
  DatePicker,
} from 'antd';
import type { UploadChangeParam, UploadFile } from 'antd/es/upload';
import type { RcFile } from 'antd/es/upload/interface';
import { useTranslate, IResourceComponentsProps, useApiUrl } from '@pankod/refine-core';
import { Edit, useForm, useSelect, message } from '@pankod/refine-antd';
import { ConveyanceStatus } from 'types/ownership/conveyanceStatus';
import { ConveyanceType } from 'types/ownership/conveyanceType';
import { ConveyanceAttachmentType } from 'types/ownership/conveyanceAttachmentType';
import { AttachmentValidationStatus } from 'types/ownership/attachmentValidationStatus';
import { CustomUploadFile } from 'types/ownership/customUploadFile';
import dayjs from 'dayjs';
import { User } from 'interfaces';

interface Conveyance {
  id: number;
  ownership_id: number;
  grant_to: number;
  status: ConveyanceStatus;
  type: ConveyanceType;
  title: string;
  description: string | null;
  amount: number;
  interval: number;
  auto_renew?: boolean;
  start: string | null;
  end: string | null;
  note: string | null;
  updated_by: number;
  created_by: number;
  created_at: string | null;
  updated_at: string | null;
  deleted_at: string | null;
  attachments: Attachment[];
}

interface Attachment {
  path?: string;
  conveyance_type?: ConveyanceAttachmentType | null;
  custom_type?: string | null;
  details?: string | null;
  document_number?: string | null;
  issue_date?: string | null;
  expiration_date?: string | null;
  validation_status?: AttachmentValidationStatus;
  observations?: string | null;
}

const beforeUpload = (file: RcFile) => {
  const allowedTypes = [
    'application/pdf',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'image/jpeg',
    'image/png',
    'image/gif',
    'image/webp',
  ];
  const isValidType = allowedTypes.includes(file.type);

  if (!isValidType) {
    message.error('Format non supporté !');
    return Upload.LIST_IGNORE;
  }

  const isLt5M = file.size / 1024 / 1024 < 5;
  if (!isLt5M) {
    message.error('Le fichier doit être inférieur à 5 Mo !');
  }

  return isValidType && isLt5M;
};

export const ConveyanceEdit: React.FC<IResourceComponentsProps> = () => {
  const t = useTranslate();
  const apiUrl = useApiUrl();
  const { TextArea } = Input;
  const { form, formProps, queryResult, onFinish, saveButtonProps } = useForm<Conveyance>({
    resource: 'conveyance',
    action: 'edit',
    redirect: 'list', // Rediriger vers la liste après la soumission
  });
  const [conveyanceStatus, setConveyanceStatus] = useState<ConveyanceStatus | undefined>(
    queryResult?.data?.data?.status,
  );
  const [conveyanceAttachments, setConveyanceAttachments] = useState<CustomUploadFile[]>(
    queryResult?.data?.data?.attachments?.map((a: Attachment, index: number) => ({
      uid: `attach-${index}-${Date.now()}`,
      name: a.path?.split('/').pop() || 'document',
      status: 'done' as const,
      url: `${process.env.REACT_APP_SERVER}${a.path || ''}`,
      path: a.path || '',
      conveyance_type: a.conveyance_type || ConveyanceAttachmentType.Acte_de_vente,
      custom_type: a.custom_type || null,
      details: a.details || '',
      document_number: a.document_number || '',
      issue_date: a.issue_date || null,
      expiration_date: a.expiration_date || null,
      validation_status: a.validation_status || AttachmentValidationStatus.waiting,
      observations: a.observations || '',
    })) || [],
  );
  const [editConveyanceError, setEditConveyanceError] = useState(false);

  const { selectProps: userSelectProps, queryResult: userQueryResult } = useSelect<User>({
    resource: 'user',
    optionLabel: 'lastname',
    optionValue: 'id',
    defaultValue: queryResult?.data?.data?.grant_to,
  });

  const users = userQueryResult?.data?.data?.map((user: User) => ({
    id: user.id,
    name: `${user.lastname || ''} ${user.firstname || ''}`.trim() || user.phone || 'N/A',
    phone: user.phone || 'N/A',
    email: user.email || 'N/A',
  })) || [];

  const conveyanceStatusOptions = Object.values(ConveyanceStatus).map((value) => ({
    value,
    label: t(`status.${value}`, { ns: 'conveyances' }),
  }));
  const conveyanceTypeOptions = Object.values(ConveyanceType).map((value) => ({
    value,
    label: t(`types.${value}`, { ns: 'conveyances' }),
  }));
  const attachmentTypeOptions = Object.values(ConveyanceAttachmentType).map((value) => ({
    value,
    label: t(`types.${value}`, { ns: 'attachments' }),
  }));
  const attachmentStatusOptions = Object.values(AttachmentValidationStatus).map((value) => ({
    value,
    label: t(`status.${value}`, { ns: 'attachments' }),
  }));

  const conveyanceHandleChange =
    (index: number) =>
    ({ fileList: newFileList }: UploadChangeParam<UploadFile>) => {
      const updatedFileList: CustomUploadFile[] = newFileList.map((file) => {
        if (file.response && file.response.filePath) {
          return {
            ...file,
            url: process.env.REACT_APP_SERVER + file.response.filePath,
            path: file.response.filePath,
            status: 'done' as const,
            conveyance_type: ConveyanceAttachmentType.Acte_de_vente,
            custom_type: null,
          } as CustomUploadFile;
        }
        return {
          ...file,
          conveyance_type: ConveyanceAttachmentType.Acte_de_vente,
          custom_type: null,
        } as CustomUploadFile;
      });

      const currentAttachments = form.getFieldValue('attachments') || [];
      currentAttachments[index] = {
        ...currentAttachments[index],
        path: updatedFileList[0]?.path || '',
        conveyance_type: updatedFileList[0]?.conveyance_type || ConveyanceAttachmentType.Acte_de_vente,
        custom_type: updatedFileList[0]?.custom_type || null,
        details: updatedFileList[0]?.details,
        document_number: updatedFileList[0]?.document_number,
        issue_date: updatedFileList[0]?.issue_date,
        expiration_date: updatedFileList[0]?.expiration_date,
        validation_status: updatedFileList[0]?.validation_status || AttachmentValidationStatus.waiting,
        observations: updatedFileList[0]?.observations,
      };
      form.setFieldsValue({ attachments: currentAttachments });
      setConveyanceAttachments((prev) => {
        const newAttachments = [...prev];
        newAttachments[index] = updatedFileList[0] || {
          uid: `attach-${index}-${Date.now()}`,
          name: '',
          status: 'error' as const,
          conveyance_type: ConveyanceAttachmentType.Acte_de_vente,
          custom_type: null,
        };
        return newAttachments;
      });
    };

  const handleRemoveAttachment = async (name: number, index: number, remove: (index: number) => void) => {
    const file = conveyanceAttachments[index];
    const conveyanceId = queryResult?.data?.data?.id;
    const ownershipId = queryResult?.data?.data?.ownership_id;

    setConveyanceAttachments((prev) => {
      const newAttachments = [...prev];
      newAttachments.splice(index, 1);
      return newAttachments;
    });
    remove(name);
    message.success(t('notifications.deleteSuccess', { ns: 'common' }));

    if (file?.path && conveyanceId && ownershipId) {
      try {
        const response = await fetch(`${apiUrl}/attachments/by-path`, {
          method: 'DELETE',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
          body: JSON.stringify({
            path: file.path,
            ownership_id: ownershipId,
            conveyance_id: conveyanceId,
          }),
        });
        if (!response.ok) {
          throw new Error('Échec de la suppression de l\'attachment');
        }
      } catch (err) {
        console.error('Erreur lors de la suppression serveur:', err);
        message.error(t('errors.attachment_delete_failed', { ns: 'attachments' }, "Échec de la suppression de l'attachment côté serveur."));
      }
    }
  };

  const handleFinish = async (values: any) => {
    setEditConveyanceError(false);
    try {
      const formData = {
        ...values,
        ownership_id: queryResult?.data?.data?.ownership_id,
        start: values.start ? values.start.toISOString() : null,
        end: values.end ? values.end.toISOString() : null,
        amount: values.amount ? parseFloat(values.amount) : 0,
        interval: values.interval ? parseInt(values.interval, 10) : 0,
        attachments: (values.attachments || []).map((a: any) => ({
          ...a,
          path: a.path || a.response?.filePath || null,
          issue_date: a.issue_date
            ? dayjs.isDayjs(a.issue_date)
              ? a.issue_date.toISOString()
              : typeof a.issue_date === 'string'
              ? new Date(a.issue_date).toISOString()
              : null
            : null,
          expiration_date: a.expiration_date
            ? dayjs.isDayjs(a.expiration_date)
              ? a.expiration_date.toISOString()
              : typeof a.expiration_date === 'string'
              ? new Date(a.expiration_date).toISOString()
              : null
            : null,
          conveyance_type: a.conveyance_type || ConveyanceAttachmentType.Acte_de_vente,
          custom_type: a.custom_type || null,
          validation_status: a.validation_status || AttachmentValidationStatus.waiting,
          observations: a.observations || null,
          document_number: a.document_number || null,
          details: a.details || null,
        })),
        updated_by: queryResult?.data?.data?.updated_by,
      };
      await onFinish(formData);
    } catch (error) {
      console.error('Erreur lors de la soumission:', error);
      setEditConveyanceError(true);
      message.error(t('messages.conveyance_update_failed', { ns: 'conveyances' }, 'Erreur lors de la mise à jour de la conveyance.'));
    }
  };

  return (
    <Edit saveButtonProps={saveButtonProps}>
      {editConveyanceError && (
        <Alert
          message={t('fields.conveyance_error', { ns: 'ownerships' })}
          type="error"
          showIcon
          closable
        />
      )}
      <Form
        {...formProps}
        form={form}
        layout="vertical"
        onFinish={handleFinish}
        initialValues={{
          ...queryResult?.data?.data,
          start: queryResult?.data?.data?.start ? dayjs(queryResult?.data?.data?.start) : null,
          end: queryResult?.data?.data?.end ? dayjs(queryResult?.data?.data?.end) : null,
          attachments: queryResult?.data?.data?.attachments?.map((a: Attachment) => ({
            ...a,
            issue_date: a.issue_date ? dayjs(a.issue_date) : null,
            expiration_date: a.expiration_date ? dayjs(a.expiration_date) : null,
            conveyance_type: a.conveyance_type || ConveyanceAttachmentType.Acte_de_vente,
          })),
        }}
      >
        <Form.Item hidden name="id">
          <InputNumber hidden />
        </Form.Item>
        <Row>
          <Col className="gutter-row" span={24} style={{ marginBottom: '8px' }}>
            <Form.Item name="auto_renew" valuePropName="checked" noStyle>
              <Checkbox>{t('fields.auto_renew', { ns: 'conveyances' })}</Checkbox>
            </Form.Item>
          </Col>
          <Col className="gutter-row" xs={24} sm={24} md={24} lg={12} xl={12}>
            <Form.Item
              label={t('fields.title', { ns: 'conveyances' })}
              name="title"
              rules={[{ required: true, message: t('errors.title_required', { ns: 'conveyances' }, 'Veuillez saisir le titre') }]}
            >
              <Input maxLength={255} disabled={conveyanceStatus === ConveyanceStatus.Granted} />
            </Form.Item>
          </Col>
          <Col className="gutter-row" xs={24} sm={24} md={24} lg={12} xl={12}>
            <Form.Item
              label={t('fields.type', { ns: 'conveyances' })}
              name="type"
              rules={[{ required: true, message: t('errors.type_required', { ns: 'conveyances' }, 'Veuillez sélectionner le type') }]}
            >
              <Select
                options={conveyanceTypeOptions}
                disabled={conveyanceStatus === ConveyanceStatus.Granted}
              />
            </Form.Item>
          </Col>
          <Col className="gutter-row" xs={24} sm={24} md={24} lg={12} xl={12}>
            <Form.Item
              label={t('fields.garant_to', { ns: 'conveyances' })}
              name="grant_to"
              rules={[{ required: true, message: t('errors.grant_to_required', { ns: 'conveyances' }, 'Veuillez sélectionner le bénéficiaire') }]}
            >
              <Select
                {...userSelectProps}
                options={users.map((user) => ({
                  value: user.id,
                  label: user.name,
                }))}
                disabled={conveyanceStatus === ConveyanceStatus.Granted}
              />
            </Form.Item>
          </Col>
          <Col className="gutter-row" xs={24} sm={24} md={24} lg={12} xl={12}>
            <Form.Item
              label={t('fields.status', { ns: 'conveyances' })}
              name="status"
              rules={[{ required: true, message: t('errors.status_required', { ns: 'conveyances' }, 'Veuillez sélectionner le statut') }]}
            >
              <Select
                options={conveyanceStatusOptions}
                disabled={conveyanceStatus === ConveyanceStatus.Granted}
                onChange={(value) => setConveyanceStatus(value)}
              />
            </Form.Item>
          </Col>
          <Col className="gutter-row" xs={24} sm={24} md={24} lg={12} xl={12}>
            <Form.Item
              label={t('fields.amount', { ns: 'conveyances' })}
              name="amount"
              rules={[{ required: true, message: t('errors.amount_required', { ns: 'conveyances' }, 'Veuillez saisir le montant') }]}
            >
              <InputNumber
                style={{ width: '100%' }}
                disabled={conveyanceStatus === ConveyanceStatus.Granted}
              />
            </Form.Item>
          </Col>
          <Col className="gutter-row" xs={24} sm={24} md={24} lg={12} xl={12}>
            <Form.Item
              label={t('fields.interval', { ns: 'conveyances' })}
              name="interval"
              rules={[{ required: true, message: t('errors.interval_required', { ns: 'conveyances' }, 'Veuillez saisir l\'intervalle') }]}
            >
              <InputNumber
                style={{ width: '100%' }}
                disabled={conveyanceStatus === ConveyanceStatus.Granted}
              />
            </Form.Item>
          </Col>
          <Col className="gutter-row" xs={24} sm={24} md={24} lg={12} xl={12}>
            <Form.Item
              label={t('fields.start', { ns: 'conveyances' })}
              name="start"
              getValueProps={(value: any) => ({
                value: value ? dayjs(value) : null,
              })}
              rules={[{ required: true, message: t('errors.start_required', { ns: 'conveyances' }, 'Veuillez saisir la date de début') }]}
            >
              <DatePicker
                format="DD.MM.YYYY"
                style={{ width: '100%' }}
                disabled={conveyanceStatus === ConveyanceStatus.Granted}
              />
            </Form.Item>
          </Col>
          <Col className="gutter-row" xs={24} sm={24} md={24} lg={12} xl={12}>
            <Form.Item
              label={t('fields.end', { ns: 'conveyances' })}
              name="end"
              getValueProps={(value: any) => ({
                value: value ? dayjs(value) : null,
              })}
            >
              <DatePicker
                format="DD.MM.YYYY"
                style={{ width: '100%' }}
              />
            </Form.Item>
          </Col>
          <Col className="gutter-row" xs={24} sm={24} md={24} lg={12} xl={12}>
            <Form.Item
              label={t('fields.description', { ns: 'conveyances' })}
              name="description"
            >
              <TextArea maxLength={255} rows={3} />
            </Form.Item>
          </Col>
          <Col className="gutter-row" span={24}>
            <Form.Item label={t('fields.note', { ns: 'conveyances' })} name="note">
              <TextArea />
            </Form.Item>
          </Col>
          <Col className="gutter-row" span={24}>
            <Form.List name="attachments">
              {(fields, { add, remove }) => (
                <>
                  {fields.map(({ key, name, ...restField }, index) => (
                    <Row
                      key={key}
                      gutter={[16, 16]}
                      style={{
                        marginBottom: '16px',
                        borderBottom: '1px solid #f0f0f0',
                        paddingBottom: '16px',
                      }}
                    >
                      <Col xs={24} sm={24} md={12} lg={12} xl={12}>
                        <Form.Item
                          {...restField}
                          label={t('fields.attachment_file', { ns: 'attachments' })}
                          name={[name, 'path']}
                          rules={[{ required: true, message: t('errors.attachment_required', { ns: 'attachments' }, 'Veuillez télécharger un fichier') }]}
                        >
                          <Upload
                            action={`${apiUrl}/media/upload`}
                            data={{ table: `ownerships/${queryResult?.data?.data?.ownership_id}/conveyance` }}
                            listType="text"
                            accept="application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,image/jpeg,image/png,image/gif,image/webp"
                            onChange={conveyanceHandleChange(index)}
                            onRemove={() => {
                              handleRemoveAttachment(name, index, remove);
                              return false;
                            }}
                            fileList={
                              conveyanceAttachments[index] ? [conveyanceAttachments[index]] : []
                            }
                            maxCount={1}
                            beforeUpload={beforeUpload}
                          >
                            {(!conveyanceAttachments[index] ||
                              conveyanceAttachments[index].status !== 'done') && (
                              <Button style={{ borderRadius: '6px', padding: '4px 15px' }}>
                                {t('buttons.upload', { ns: 'common' }, 'Télécharger')}
                              </Button>
                            )}
                          </Upload>
                        </Form.Item>
                      </Col>
                      <Col xs={24} sm={24} md={12} lg={12} xl={12}>
                        <Form.Item
                          {...restField}
                          label={t('type', { ns: 'attachments' })}
                          name={[name, 'conveyance_type']}
                          rules={[{ required: true, message: t('errors.type_required', { ns: 'attachments' }, 'Veuillez sélectionner le type') }]}
                        >
                          <Select
                            options={attachmentTypeOptions}
                            onChange={(value) => {
                              const currentAttachments =
                                form.getFieldValue('attachments') || [];
                              currentAttachments[index] = {
                                ...currentAttachments[index],
                                conveyance_type: value,
                                custom_type:
                                  value !== ConveyanceAttachmentType.Autre
                                    ? null
                                    : currentAttachments[index].custom_type,
                              };
                              form.setFieldsValue({ attachments: currentAttachments });
                            }}
                          />
                        </Form.Item>
                      </Col>
                      {(() => {
                        const attachmentType = form.getFieldValue([
                          'attachments',
                          name,
                          'conveyance_type',
                        ]);
                        const isAutre = attachmentType === ConveyanceAttachmentType.Autre;
                        const customTypeValue = form.getFieldValue([
                          'attachments',
                          name,
                          'custom_type',
                        ]);
                        return isAutre ? (
                          <Col xs={24} sm={24} md={12} lg={12} xl={12}>
                            <Form.Item
                              {...restField}
                              label={t('fields.custom_type', { ns: 'attachments' })}
                              name={[name, 'custom_type']}
                              rules={[
                                {
                                  required: true,
                                  message: t('errors.custom_type_required', { ns: 'attachments' }, 'Veuillez saisir un type personnalisé pour "Autres"'),
                                },
                              ]}
                              initialValue={customTypeValue}
                            >
                              <Input maxLength={255} />
                            </Form.Item>
                          </Col>
                        ) : null;
                      })()}
                      <Col xs={24} sm={24} md={12} lg={12} xl={12}>
                        <Form.Item
                          {...restField}
                          label={t('fields.details', { ns: 'attachments' })}
                          name={[name, 'details']}
                        >
                          <TextArea maxLength={500} />
                        </Form.Item>
                      </Col>
                      <Col xs={24} sm={24} md={12} lg={12} xl={12}>
                        <Form.Item
                          {...restField}
                          name={[name, 'document_number']}
                          label={t('fields.document_number', { ns: 'attachments' })}
                        >
                          <Input maxLength={255} />
                        </Form.Item>
                      </Col>
                      <Col xs={24} sm={24} md={12} lg={12} xl={12}>
                        <Form.Item
                          {...restField}
                          label={t('fields.issue_date', { ns: 'attachments' })}
                          name={[name, 'issue_date']}
                          getValueProps={(value: any) => ({ value: value ? dayjs(value) : null })}
                        >
                          <DatePicker format="DD.MM.YYYY" style={{ width: '100%' }} />
                        </Form.Item>
                      </Col>
                      <Col xs={24} sm={24} md={12} lg={12} xl={12}>
                        <Form.Item
                          {...restField}
                          label={t('fields.expiration_date', { ns: 'attachments' })}
                          name={[name, 'expiration_date']}
                          getValueProps={(value: any) => ({ value: value ? dayjs(value) : null })}
                        >
                          <DatePicker
                            format="DD.MM.YYYY"
                            style={{ width: '100%' }}
                            disabled={conveyanceStatus === ConveyanceStatus.Granted}
                          />
                        </Form.Item>
                      </Col>
                      <Col xs={24} sm={24} md={24} lg={24} xl={24}>
                        <Form.Item
                          {...restField}
                          label={t('fields.observations', { ns: 'attachments' })}
                          name={[name, 'observations']}
                        >
                          <TextArea maxLength={1000} />
                        </Form.Item>
                      </Col>
                      <Col xs={24} style={{ textAlign: 'right' }}>
                        <Button
                          danger
                          onClick={() => handleRemoveAttachment(name, index, remove)}
                        >
                          {t('buttons.remove_attachment', { ns: 'attachments' }, 'Supprimer la pièce jointe')}
                        </Button>
                      </Col>
                    </Row>
                  ))}
                  <Form.Item>
                    <Button
                      type="dashed"
                      onClick={() => {
                        add({
                          conveyance_type: ConveyanceAttachmentType.Acte_de_vente,
                          validation_status: AttachmentValidationStatus.waiting,
                          custom_type: null,
                        });
                        setConveyanceAttachments((prev) => [
                          ...prev,
                          {
                            uid: `attach-${fields.length}-${Date.now()}`,
                            name: '',
                            status: 'error' as const,
                            conveyance_type: ConveyanceAttachmentType.Acte_de_vente,
                            custom_type: null,
                          },
                        ]);
                      }}
                      block
                      icon={<i className="anticon anticon-plus" />}
                    >
                      {t('buttons.add_attachment', { ns: 'attachments' }, 'Ajouter une pièce jointe')}
                    </Button>
                  </Form.Item>
                </>
              )}
            </Form.List>
          </Col>
        </Row>
      </Form>
    </Edit>
  );
};
